import { Routes, Route, Navigate } from 'react-router-dom';
import PrivateRoute from './components/PrivateRoute';
import GlassGate from './components/GlassGate';
import InstallPrompt from './components/InstallPrompt';
import AppLayout from './components/AppLayout';
import LoginPage from './features/auth/LoginPage';
import SetPasswordPage from './features/auth/SetPasswordPage';
import ForgotPasswordPage from './features/auth/ForgotPasswordPage';
import EvidencePage from './features/tasks/EvidencePage';
import DashboardPage from './features/dashboard/DashboardPage';
import BirthdaysMonthPage from './features/dashboard/BirthdaysMonthPage';
import MembersListPage from './features/members/pages/MembersListPage';
import MemberDetailPage from './features/members/pages/MemberDetailPage';
import ClassesCalendarPage from './features/classes/pages/ClassesCalendarPage';
import ClassDetailPage from './features/classes/pages/ClassDetailPage';
import ProspectsPage from './features/classes/pages/ProspectsPage';
import CloneClassesPage from './features/classes/pages/CloneClassesPage';
import FinancesPage from './features/finances/pages/FinancesPage';
import CajaPage from './features/finances/pages/CajaPage';
import DesfasadosPage from './features/finances/pages/DesfasadosPage';
import VitrinaPage from './features/finances/pages/VitrinaPage';
import AyudaPage from './features/help/AyudaPage';
import PeticionesPage from './features/peticiones/PeticionesPage';
import WhatsAppPage from './features/whatsapp/WhatsAppPage';
import ReceiptPage from './features/finances/pages/ReceiptPage';
import ReportsPage from './features/reports/pages/ReportsPage';
import UsersPage from './features/users/UsersPage';
import MiPlanPage from './features/billing/MiPlanPage';
import MiEstudioPage from './features/estudio/pages/MiEstudioPage';
import EventsListPage from './features/events/pages/EventsListPage';
import EventDetailPage from './features/events/pages/EventDetailPage';
import EventTicketReceiptPage from './features/events/pages/EventTicketReceiptPage';
import MessagesPage from './features/messages/pages/MessagesPage';
import StudentLayout from './features/portal/StudentLayout';
import StudentHome from './features/portal/pages/StudentHome';
import StudentClasses from './features/portal/pages/StudentClasses';
import StudentEvents from './features/portal/pages/StudentEvents';
import StudentPayments from './features/portal/pages/StudentPayments';
import StudentAttendance from './features/portal/pages/StudentAttendance';
import StudentMessages from './features/portal/pages/StudentMessages';
import TeacherLayout from './features/portal/TeacherLayout';
import TeacherClasses from './features/portal/pages/TeacherClasses';
import MembershipsPage from './features/memberships/MembershipsPage';
import TeachersListPage from './features/teachers/pages/TeachersListPage';
import TeacherDetailPage from './features/teachers/pages/TeacherDetailPage';
import { useAuth } from './context/AuthContext';
import { DebtFlagsProvider } from './context/DebtFlagsContext';

export default function App() {
  const { user, loading, modules } = useAuth();
  const role = user?.role;
  // Espera: sesión + (para operadores) permisos de módulos. `modules===undefined` = aún cargando →
  // no pintamos la consola hasta saber qué gatear (evita el flash de ver un módulo y que desaparezca).
  if (loading || (user && role !== 'student' && modules === undefined)) {
    return <div className="flex h-full items-center justify-center bg-canvas text-content-3">Cargando…</div>;
  }

  return (
    // Banderas de adeudo para TODA la consola: se cargan una vez y cualquier PersonAvatar las lee.
    // Para el alumno el proveedor no pide nada (y el backend tampoco se lo daría).
    <DebtFlagsProvider>
    <GlassGate />
    {user && <InstallPrompt />}
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Acceso de alumnos/profes (públicas, fuera del gate por rol).
          Núcleo (correo): /activar?token=… y /restablecer?token=…
          Compat (path param): /activar/:token y /recuperar/:token */}
      <Route path="/activar" element={<SetPasswordPage mode="invite" />} />
      <Route path="/activar/:token" element={<SetPasswordPage mode="invite" />} />
      <Route path="/restablecer" element={<SetPasswordPage mode="reset" />} />
      <Route path="/recuperar/:token" element={<SetPasswordPage mode="reset" />} />
      <Route path="/recuperar" element={<ForgotPasswordPage />} />
      {/* PÚBLICA: el celular sube la foto de evidencia de una tarea con el token del QR (F4) */}
      <Route path="/evidencia/:token" element={<EvidencePage />} />

      {role === 'student' ? (
        // ── Portal del ALUMNO (F9) ──
        <Route element={<PrivateRoute><StudentLayout /></PrivateRoute>}>
          <Route path="/" element={<StudentHome />} />
          <Route path="/mis-clases" element={<StudentClasses />} />
          <Route path="/mis-eventos" element={<StudentEvents />} />
          <Route path="/mis-pagos" element={<StudentPayments />} />
          <Route path="/mi-asistencia" element={<StudentAttendance />} />
          <Route path="/mis-mensajes" element={<StudentMessages />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      ) : role === 'teacher' ? (
        // ── Portal del PROFESOR ──
        <Route element={<PrivateRoute><TeacherLayout /></PrivateRoute>}>
          <Route path="/" element={<TeacherClasses />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      ) : (
        // ── Panel ADMIN / profesor ──
        <Route element={<PrivateRoute><AppLayout /></PrivateRoute>}>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/cumpleanos" element={<BirthdaysMonthPage />} />
          <Route path="/alumnos" element={<MembersListPage />} />
          <Route path="/alumnos/:id" element={<MemberDetailPage />} />
          <Route path="/clases" element={<ClassesCalendarPage />} />
          {/* Asistente de arranque de ciclo (la ruta estática gana a /clases/:id por especificidad) */}
          <Route path="/clases/clonar" element={<CloneClassesPage />} />
          <Route path="/clases/:id" element={<ClassDetailPage />} />
          <Route path="/prospectos" element={<ProspectsPage />} />
          <Route path="/profesores" element={<TeachersListPage />} />
          <Route path="/profesores/:id" element={<TeacherDetailPage />} />
          <Route path="/finanzas" element={<FinancesPage />} />
          <Route path="/caja" element={<CajaPage />} />
          <Route path="/desfasados" element={<DesfasadosPage />} />
          <Route path="/vitrina" element={<VitrinaPage />} />
          <Route path="/recibo/:id" element={<ReceiptPage />} />
          <Route path="/eventos" element={<EventsListPage />} />
          <Route path="/eventos/:id" element={<EventDetailPage />} />
          {/* Recibo de una compra de boletos del festival (28-oct-2026): imprimir / guardar PDF */}
          <Route path="/eventos/:id/recibo/:tid" element={<EventTicketReceiptPage />} />
          <Route path="/mensajes" element={<MessagesPage />} />
          <Route path="/reportes" element={<ReportsPage />} />
          <Route path="/usuarios" element={<UsersPage />} />
          <Route path="/mi-plan" element={<MiPlanPage />} />
          <Route path="/mi-estudio" element={<MiEstudioPage />} />
          <Route path="/membresias" element={<MembershipsPage />} />
          <Route path="/ayuda" element={<AyudaPage />} />
          {/* Mis peticiones (F2 peticiones, 11-oct-2026): entrada propia del menú, separada de Ayuda */}
          <Route path="/peticiones" element={<PeticionesPage />} />
          <Route path="/whatsapp" element={<WhatsAppPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      )}
    </Routes>
    </DebtFlagsProvider>
  );
}
